import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Eye, Mail, Calendar, Inbox,
  CheckCircle, Clock, AlertCircle, MessageCircle, Volume2, Send, UserPlus, Trash2, UserCheck,
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchCoachConfig, fetchRespondentAccess, upsertRespondentAccess } from '../../lib/ai-coach';
import { InviteRespondentModal } from '../../components/coach/InviteRespondentModal';

interface SharedRespondent {
  shareId: string;
  resultId: string;
  name: string;
  email: string;
  message: string | null;
  status: string;
  createdAt: string;
  completedAt: string;
}

interface Respondent {
  id: string;
  userId: string;
  email: string;
  name: string;
  createdAt: Date;
  hasCompletedAssessment: boolean;
  completedAt?: Date;
  hasAiAnalysis: boolean;
  aiAnalysisVisible: boolean;
  chatEnabled: boolean;
  voiceEnabled: boolean;
  isActive: boolean;
}

export function RespondentsList() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'disabled' | 'completed' | 'pending'>('active');
  const [coachHasAIAccess, setCoachHasAIAccess] = useState(false);
  const [aiCoachEnabled, setAiCoachEnabled] = useState(false);
  const [currentCoachId, setCurrentCoachId] = useState<string | null>(null);
  const [assessmentCode, setAssessmentCode] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState('');
  const [activeTab, setActiveTab] = useState<'respondents' | 'shared'>('respondents');
  const [sharedResults, setSharedResults] = useState<SharedRespondent[]>([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/login');
          return;
        }

        const aiAccess = await checkCoachAIAccess(user.id);
        setCoachHasAIAccess(aiAccess);

        const { data: coach } = await supabase
          .from('coaches')
          .select('id, assessment_code')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) {
          navigate('/login');
          return;
        }

        setCurrentCoachId(coach.id);
        setAssessmentCode(coach.assessment_code || null);

        const [respondentsRes, aiConfig, aiAccessList] = await Promise.all([
          supabase
            .from('respondents')
            .select('id, user_id, created_at, is_active, ai_analysis_visible, users!respondents_user_id_fkey(full_name, email)')
            .eq('coach_id', coach.id),
          fetchCoachConfig(coach.id),
          fetchRespondentAccess(coach.id),
        ]);

        const respondentsData = respondentsRes.data;
        setAiCoachEnabled(aiConfig?.isEnabled || false);

        const accessMap = new Map(aiAccessList.map(a => [a.respondentId, a]));
        const userIds = (respondentsData || []).map(r => r.user_id);

        const completedResults = new Map<string, { completedAt: Date; hasAi: boolean }>();
        if (userIds.length > 0) {
          const { data: results } = await supabase
            .from('results')
            .select('user_id, completed_at, ai_analysis')
            .in('user_id', userIds);

          (results || []).forEach(result => {
            const existing = completedResults.get(result.user_id);
            const completedAt = result.completed_at ? new Date(result.completed_at) : null;
            if (completedAt && (!existing || completedAt > existing.completedAt)) {
              completedResults.set(result.user_id, {
                completedAt,
                hasAi: !!result.ai_analysis,
              });
            }
          });
        }

        const mapped = (respondentsData || []).map(r => {
          const resultInfo = completedResults.get(r.user_id);
          const access = accessMap.get(r.id);
          return {
            id: r.id,
            userId: r.user_id,
            email: (r.users as any)?.email || '',
            name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
            createdAt: new Date(r.created_at || Date.now()),
            hasCompletedAssessment: !!resultInfo,
            completedAt: resultInfo?.completedAt,
            hasAiAnalysis: resultInfo?.hasAi || false,
            aiAnalysisVisible: r.ai_analysis_visible || false,
            chatEnabled: access?.chatEnabled || false,
            voiceEnabled: access?.voiceEnabled || false,
            isActive: r.is_active !== false,
          };
        }) as Respondent[];

        setRespondents(mapped);

        const { data: sharedData } = await supabase
          .rpc('fetch_received_shares', {
            p_recipient_type: 'coach',
            p_recipient_id: coach.id,
          });

        const sharedMapped = (sharedData || []).map((row: any) => ({
          shareId: row.share_id,
          resultId: row.result_id,
          name: row.sharer_name || 'Respondent',
          email: row.sharer_email || '',
          message: row.message,
          status: row.status,
          createdAt: row.created_at,
          completedAt: row.result_completed_at || row.created_at,
        })) as SharedRespondent[];

        setSharedResults(sharedMapped);
      } catch (err) {
        console.error('Error loading respondents:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const triggerAiGeneration = async (userId: string) => {
    try {
      const { data: result } = await supabase
        .from('results')
        .select('id, scores, harmonic_scores, ai_analysis')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!result || result.ai_analysis) return;

      const { buildAIPrompt } = await import('../../components/ai/buildAIPrompt');
      const scores = (result.scores as any[]) || [];
      const harmonicScores = (result.harmonic_scores as any[]) || [];
      const prompt = buildAIPrompt(
        scores,
        harmonicScores,
        'provide a comprehensive analysis of this person\'s potential, strengths, and areas for development. Include specific recommendations for personal growth.'
      );

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) return;

      const data = await response.json();
      await supabase
        .from('results')
        .update({
          ai_analysis: data.analysis,
          ai_analysis_generated_at: new Date().toISOString(),
        })
        .eq('id', result.id);
    } catch (err) {
      console.error('Error auto-generating AI analysis:', err);
    }
  };

  const toggleAiVisibility = async (respondent: Respondent) => {
    const newValue = !respondent.aiAnalysisVisible;

    setTogglingIds(prev => new Set(prev).add(respondent.id));

    try {
      const { error } = await supabase
        .from('respondents')
        .update({ ai_analysis_visible: newValue })
        .eq('id', respondent.id);

      if (error) throw error;

      setRespondents(prev =>
        prev.map(r => r.id === respondent.id ? { ...r, aiAnalysisVisible: newValue } : r)
      );
    } catch (err) {
      console.error('Error toggling AI visibility:', err);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(respondent.id);
        return next;
      });
    }
  };

  const toggleRespondentAccess = async (respondent: Respondent) => {
    const newValue = !respondent.isActive;
    setTogglingIds(prev => new Set(prev).add(`${respondent.id}-active`));

    try {
      const { error } = await supabase.rpc('set_respondent_active', {
        p_respondent_id: respondent.id,
        p_is_active: newValue,
      });

      if (error) throw error;

      setRespondents(prev =>
        prev.map(r => r.id === respondent.id ? { ...r, isActive: newValue } : r)
      );
    } catch (err) {
      console.error('Error toggling respondent access:', err);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(`${respondent.id}-active`);
        return next;
      });
    }
  };

  const toggleAiCoachAccess = async (respondent: Respondent, field: 'chatEnabled' | 'voiceEnabled') => {
    if (!currentCoachId) return;
    const newValue = !respondent[field];

    setTogglingIds(prev => new Set(prev).add(`${respondent.id}-${field}`));

    try {
      await upsertRespondentAccess(currentCoachId, respondent.id, {
        chatEnabled: field === 'chatEnabled' ? newValue : respondent.chatEnabled,
        voiceEnabled: field === 'voiceEnabled' ? newValue : respondent.voiceEnabled,
        maxChatMessages: 0,
        maxVoiceMinutes: 0,
        maxTokensPerMonth: 0,
      });

      setRespondents(prev =>
        prev.map(r => r.id === respondent.id ? { ...r, [field]: newValue } : r)
      );
    } catch (err) {
      console.error('Error toggling AI Coach access:', err);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(`${respondent.id}-${field}`);
        return next;
      });
    }
  };

  const resendCredentials = async (respondent: Respondent) => {
    if (!confirm(`This will reset ${respondent.name}'s password and send them a new login email. Continue?`)) return;
    setResendingIds(prev => new Set(prev).add(respondent.id));
    try {
      let assessmentCode: string | undefined;
      if (currentCoachId) {
        const { data: coach } = await supabase
          .from('coaches')
          .select('assessment_code')
          .eq('id', currentCoachId)
          .maybeSingle();
        assessmentCode = coach?.assessment_code || undefined;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'resend-credentials-respondent',
            recipientEmail: respondent.email,
            recipientName: respondent.name,
            userId: respondent.userId,
            loginUrl: 'https://my.nlpos.app/login',
            assessmentCode,
          }),
        }
      );
      if (response.ok) {
        alert('Login details sent successfully!');
      } else {
        const err = await response.json();
        alert('Failed to send: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to send login details');
    } finally {
      setResendingIds(prev => {
        const next = new Set(prev);
        next.delete(respondent.id);
        return next;
      });
    }
  };

  const handleDeleteRespondent = async (respondent: Respondent) => {
    const confirmed = window.confirm(
      `Remove ${respondent.name} from your respondents list?\n\nThis will remove them from your list and they will no longer appear in your Breakthrough Reports area. Their account and assessment data will remain intact — you can re-invite them anytime.\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingIds(prev => new Set(prev).add(respondent.id));
    setDeleteError('');

    try {
      const { error } = await supabase
        .from('respondents')
        .delete()
        .eq('id', respondent.id);

      if (error) throw error;

      setRespondents(prev => prev.filter(r => r.id !== respondent.id));
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to remove respondent. Please try again.');
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(respondent.id);
        return next;
      });
    }
  };

  const filteredRespondents = respondents.filter(r => {
    const matchesSearch =
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.email.toLowerCase().includes(searchQuery.toLowerCase());

    if (filter === 'active') return matchesSearch && r.isActive;
    if (filter === 'disabled') return matchesSearch && !r.isActive;
    if (filter === 'completed') return matchesSearch && r.hasCompletedAssessment;
    if (filter === 'pending') return matchesSearch && !r.hasCompletedAssessment;
    return matchesSearch;
  });

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading respondents..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <AnimatePresence>
        {showInviteModal && assessmentCode && (
          <InviteRespondentModal
            assessmentCode={assessmentCode}
            coachName={user?.user_metadata?.full_name || user?.email || ''}
            onClose={() => setShowInviteModal(false)}
          />
        )}
      </AnimatePresence>
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-warm-500 rounded-xl shadow-soft">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Respondents</h1>
                <p className="freud-text-sm text-chocolate-500">
                  {respondents.length} respondents{sharedResults.length > 0 ? ` · ${sharedResults.length} shared` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-cream-100 rounded-xl p-1 gap-1">
                <button
                  onClick={() => setActiveTab('respondents')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'respondents' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  <Users className="w-4 h-4" />
                  My Respondents
                </button>
                <button
                  onClick={() => setActiveTab('shared')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'shared' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  <Inbox className="w-4 h-4" />
                  Shared Results
                  {sharedResults.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-sage-100 text-sage-600 text-[10px] font-bold">
                      {sharedResults.length}
                    </span>
                  )}
                </button>
              </div>
              {activeTab === 'respondents' && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="freud-btn-primary flex items-center gap-2 flex-shrink-0"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'shared' ? (
        <div className="freud-card-elevated mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Shared By</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Email</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Message</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Status</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Completed</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {sharedResults.map((share) => (
                  <tr key={share.shareId} className="hover:bg-cream-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-chocolate-900">{share.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Mail className="w-4 h-4" />
                        {share.email || '--'}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="text-sm text-chocolate-500 truncate">
                        {share.message ? `"${share.message}"` : '--'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {share.status === 'accepted' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-700 rounded-full text-sm">
                          <CheckCircle className="w-3 h-3" />
                          Accepted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 rounded-full text-sm">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Calendar className="w-4 h-4" />
                        {new Date(share.completedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <Link
                          to={`/coach/shared-results/${share.shareId}`}
                          className="flex items-center gap-2 px-3 py-1.5 text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Results
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sharedResults.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No shared results yet
            </div>
          )}
        </div>
        ) : (
        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Search respondents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'active', 'disabled', 'completed', 'pending'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                    filter === f
                      ? 'bg-sage-600 text-white'
                      : 'bg-cream-200 text-chocolate-600 hover:bg-cream-300'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'active' ? 'Active' : f === 'disabled' ? 'Disabled' : f === 'completed' ? 'Completed' : 'Pending'}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Respondent</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Email</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Status</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Joined</th>
                  {coachHasAIAccess && (
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">AI Analysis</th>
                  )}
                  {aiCoachEnabled && (
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">AI Coach</th>
                  )}
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredRespondents.map((respondent) => (
                  <tr key={respondent.id} className="hover:bg-cream-50">
                    <td className={`px-6 py-4 ${!respondent.isActive ? 'opacity-60' : ''}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-chocolate-900">{respondent.name}</span>
                        {!respondent.isActive && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                            Disabled
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${!respondent.isActive ? 'opacity-60' : ''}`}>
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Mail className="w-4 h-4" />
                        {respondent.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {respondent.hasCompletedAssessment ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-700 rounded-full text-sm">
                          <CheckCircle className="w-3 h-3" />
                          Completed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warm-100 text-warm-700 rounded-full text-sm">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Calendar className="w-4 h-4" />
                        {respondent.createdAt.toLocaleDateString()}
                      </div>
                    </td>
                    {coachHasAIAccess && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          {respondent.hasCompletedAssessment ? (
                            <button
                              onClick={() => toggleAiVisibility(respondent)}
                              disabled={togglingIds.has(respondent.id)}
                              className="group flex items-center gap-2"
                            >
                              <div
                                className={`relative w-11 h-6 rounded-full transition-colors ${
                                  respondent.aiAnalysisVisible ? 'bg-sage-500' : 'bg-gray-300'
                                } ${togglingIds.has(respondent.id) ? 'opacity-60' : ''}`}
                              >
                                <div
                                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                    respondent.aiAnalysisVisible ? 'translate-x-[22px]' : 'translate-x-0.5'
                                  }`}
                                />
                              </div>
                              <span className={`text-xs font-medium ${
                                respondent.aiAnalysisVisible ? 'text-sage-600' : 'text-gray-400'
                              }`}>
                                {respondent.aiAnalysisVisible ? 'Visible' : 'Hidden'}
                              </span>
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">--</span>
                          )}
                        </div>
                      </td>
                    )}
                    {aiCoachEnabled && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => toggleAiCoachAccess(respondent, 'chatEnabled')}
                            disabled={togglingIds.has(`${respondent.id}-chatEnabled`)}
                            className="group flex items-center gap-1.5"
                            title="Toggle chat access"
                          >
                            <MessageCircle className={`w-4 h-4 ${respondent.chatEnabled ? 'text-sage-600' : 'text-gray-300'}`} />
                            <div
                              className={`relative w-9 h-5 rounded-full transition-colors ${
                                respondent.chatEnabled ? 'bg-sage-500' : 'bg-gray-300'
                              } ${togglingIds.has(`${respondent.id}-chatEnabled`) ? 'opacity-60' : ''}`}
                            >
                              <div
                                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                                  respondent.chatEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                                }`}
                              />
                            </div>
                          </button>
                          <button
                            onClick={() => toggleAiCoachAccess(respondent, 'voiceEnabled')}
                            disabled={togglingIds.has(`${respondent.id}-voiceEnabled`)}
                            className="group flex items-center gap-1.5"
                            title="Toggle voice access"
                          >
                            <Volume2 className={`w-4 h-4 ${respondent.voiceEnabled ? 'text-warm-600' : 'text-gray-300'}`} />
                            <div
                              className={`relative w-9 h-5 rounded-full transition-colors ${
                                respondent.voiceEnabled ? 'bg-warm-500' : 'bg-gray-300'
                              } ${togglingIds.has(`${respondent.id}-voiceEnabled`) ? 'opacity-60' : ''}`}
                            >
                              <div
                                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                                  respondent.voiceEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                                }`}
                              />
                            </div>
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleRespondentAccess(respondent)}
                          disabled={togglingIds.has(`${respondent.id}-active`)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                            respondent.isActive
                              ? 'text-gray-600 hover:bg-gray-100'
                              : 'text-sage-600 hover:bg-sage-50'
                          }`}
                          title={respondent.isActive ? 'Disable client access' : 'Enable client access'}
                        >
                          {togglingIds.has(`${respondent.id}-active`) ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                          <span className="hidden sm:inline text-sm">{respondent.isActive ? 'Disable' : 'Enable'}</span>
                        </button>
                        <button
                          onClick={() => resendCredentials(respondent)}
                          disabled={resendingIds.has(respondent.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-warm-600 hover:bg-warm-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Resend login details"
                        >
                          <Send className="w-4 h-4" />
                          <span className="hidden sm:inline text-sm">Resend Login</span>
                        </button>
                        {respondent.hasCompletedAssessment && (
                          <Link
                            to={`/coach/results/${respondent.userId}`}
                            className="flex items-center gap-2 px-3 py-1.5 text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View Results
                          </Link>
                        )}
                        <button
                          onClick={() => handleDeleteRespondent(respondent)}
                          disabled={deletingIds.has(respondent.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Remove from respondents"
                        >
                          {deletingIds.has(respondent.id) ? (
                            <div className="w-4 h-4 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          <span className="hidden sm:inline text-sm">Remove</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {deleteError && (
            <div className="px-6 py-3 bg-red-50 border-t border-red-100 text-sm text-red-600">
              {deleteError}
            </div>
          )}

          {filteredRespondents.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No respondents found
            </div>
          )}
        </div>
        )}
      </main>
    </div>
  );
}
